import { Fragment, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { SxProps, type Theme } from '@mui/material/styles';
import Tooltip from '@watershed/ui-core/components/Tooltip';
import Typography from '@mui/material/Typography';

import dynamic from 'next/dynamic';
import Head from 'next/head';
import useKey from 'react-use/lib/useKey';

import { isProduction } from '@watershed/config/env';
import CloseIcon from '@watershed/icons/components/Close';
import GridIcon from '@watershed/icons/components/Grid';
import InboxIcon from '@watershed/icons/components/Inbox';
import LogOutIcon from '@watershed/icons/components/LogOut';
import LoopedSquareIcon from '@watershed/icons/components/LoopedSquare';
import QuestionMarkIcon from '@watershed/icons/components/QuestionMark';
import useLogout from '@watershed/shared-frontend/hooks/useLogout';
import { getRegionFromFrontend } from '@watershed/shared-frontend/utils/dataResidency';
import { useQueryParam } from '@watershed/shared-frontend/utils/queryParamHooks';
import { urlForObject } from '@watershed/shared-universal/adminRoutes';
import formatOrgName from '@watershed/shared-universal/utils/formatOrgName';
import Button from '@watershed/ui-core/components/Button';
import IconButton from '@watershed/ui-core/components/IconButton';
import KbdChip from '@watershed/ui-core/components/KbdChip';
import Logo from '@watershed/ui-core/components/Logo';
import Skeleton from '@watershed/ui-core/components/Skeleton';
import Link from '@watershed/ui-core/components/TextLink';
import UnstyledLink from '@watershed/ui-core/components/UnstyledLink';

import { GQOrgFieldsForAdminNavFragment } from '@watershed/app-admin/generated/graphql-operations';

import { ORGANIZATION_INBOX_DIALOG_QUERY_PARAM } from '../constants';
import { useAdminContext } from './AdminContext';
import { LoginAsButtons } from './LoginAsButtons';
import OrgQuickLinks from './OrgQuickLinks';

const OrganizationInbox = dynamic(() => import('./OrganizationInbox'));

const toolbarItemSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  '& svg': {
    flexShrink: 0,
    width: 32,
    height: 18,
    marginRight: 1,
    opacity: 0.5,
  },
  '&[disabled]': {
    color: (theme) => theme.palette.grey30,
  },
};

const toolbarItemOrgSx: SxProps<Theme> = {
  '& span': {
    fontWeight: 700,
  },
};

const iconButtonSx: SxProps<Theme> = {
  color: (theme) => theme.palette.common.white,
  padding: 0.5,
  '&:focus, &:active': {
    backgroundColor: 'transparent',
    color: (theme) => theme.palette.common.white,
  },
};

const skeletonDarkSx: SxProps<Theme> = {
  backgroundColor: 'rgb(215 219 225 / 25%)',
};

export default function NavBar({
  org,
  title,
  breadcrumbs,
  onQuickSwitch,
  onOpenQuickLinks,
  helpLink,
  helpLinkDescription,
}: {
  org: GQOrgFieldsForAdminNavFragment | null;
  title?: string;
  breadcrumbs: Array<{ title: string; url?: string }>;
  onQuickSwitch?: () => void;
  onOpenQuickLinks?: () => void;
  helpLink?: string;
  helpLinkDescription?: string;
}) {
  const [orgInboxDialog] = useQueryParam(ORGANIZATION_INBOX_DIALOG_QUERY_PARAM);
  const { openSideDrawer, setOpenSideDrawer } = useAdminContext();
  const handleLogout = useLogout();
  const region = getRegionFromFrontend();

  // If a dialog from the inbox is specified in the URL, default the org drawer
  // to open so the dialog appears on page load.
  // TODO: *don't* open the drawer if the user is on the OrganizationPage.
  useEffect(() => {
    if (orgInboxDialog) {
      setOpenSideDrawer('inbox');
    }
  }, [orgInboxDialog, setOpenSideDrawer]);

  useKey(
    (evt) => evt.key === 'h' && (evt.ctrlKey || evt.metaKey),
    (evt) => {
      evt.preventDefault();
      setOpenSideDrawer((d) => (d === 'context' ? null : 'context'));
    }
  );
  useKey(
    (evt) => evt.key === 'j' && (evt.ctrlKey || evt.metaKey),
    (evt) => {
      evt.preventDefault();
      setOpenSideDrawer((d) => (d === 'inbox' ? null : 'inbox'));
    }
  );

  const orgDisplayName = org ? formatOrgName(org) : '';
  const titleParts: Array<string> = [];
  if (title) {
    titleParts.push(title);
  } else if (breadcrumbs.length > 0) {
    titleParts.push(breadcrumbs[breadcrumbs.length - 1].title);
  }
  if (org) {
    titleParts.push(orgDisplayName);
  }

  return (
    <AppBar
      position="sticky"
      sx={(theme) => ({
        // update the navbar color when not on production
        backgroundColor: isProduction()
          ? theme.palette.text.primary
          : window.location.hostname.startsWith('proxy')
            ? // make especially obvious when on a prod proxy
              theme.palette.error.dark
            : // or a more gentle custom lavender on normal localhost
              // eslint-disable-next-line @watershed/no-custom-colors
              '#655399',
        zIndex: theme.zIndex.drawer + 1,
      })}
    >
      <Head>
        <title>
          {titleParts.length > 0 ? titleParts.join(' – ') : 'Admin'}
        </title>
      </Head>
      <Box paddingX={2} paddingY={1} gap={2} display="flex" alignItems="center">
        <UnstyledLink href="/">
          <Logo
            size={24}
            sx={{
              verticalAlign: 'middle',
              transition: 'transform 160ms ease-out',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
            color="white"
          />
        </UnstyledLink>

        {org && (
          <Button
            href={urlForObject('Organization', org.id)}
            color="inherit"
            variant="text"
            sx={toolbarItemOrgSx}
          >
            {orgDisplayName}
          </Button>
        )}

        {breadcrumbs.length === 0 && org === null && (
          <Box width={200}>
            <Skeleton sx={skeletonDarkSx} />
          </Box>
        )}

        {breadcrumbs.map((crumb, i) => (
          <Fragment key={`${crumb.url} ${crumb.title}`}>
            {i > 0 && <div>/</div>}
            {crumb.url ? (
              <Button
                href={crumb.url}
                color="inherit"
                variant="text"
                sx={toolbarItemSx}
              >
                {crumb.title}
              </Button>
            ) : (
              <Button
                color="inherit"
                variant="text"
                disabled
                sx={toolbarItemSx}
              >
                {crumb.title}
              </Button>
            )}
          </Fragment>
        ))}

        <div style={{ flexGrow: 1 }} />

        {isProduction() && (
          <Typography
            variant="h3"
            sx={{
              backgroundColor: 'transparent',
              color: 'white',
            }}
          >
            {region.toUpperCase()} region
          </Typography>
        )}

        {org && (
          <>
            <IconButton
              onClick={() => setOpenSideDrawer('inbox')}
              sx={iconButtonSx}
              size="large"
              tooltipProps={{ theme: 'light' }}
              tooltip={
                <>
                  Organization inbox <KbdChip label="Cmd + J" sx={{ ml: 1 }} />
                </>
              }
            >
              <InboxIcon />
            </IconButton>
            <OrgInboxDrawer
              open={openSideDrawer === 'inbox'}
              onClose={() => setOpenSideDrawer(null)}
              org={org}
            />
          </>
        )}

        {helpLink && (
          <Tooltip title={helpLinkDescription ?? 'Guides and documentation'}>
            <Link
              href={helpLink}
              sx={{ color: (theme) => theme.palette.white, marginBottom: -1 }}
            >
              <QuestionMarkIcon />
            </Link>
          </Tooltip>
        )}

        <IconButton
          onClick={onOpenQuickLinks}
          sx={iconButtonSx}
          size="large"
          tooltipProps={{ theme: 'light' }}
          tooltip={
            <>
              Quick links… <KbdChip label="Cmd + ." sx={{ ml: 1 }} />
            </>
          }
        >
          <GridIcon />
        </IconButton>

        <IconButton
          onClick={onQuickSwitch}
          sx={iconButtonSx}
          size="large"
          tooltipProps={{ theme: 'light' }}
          tooltip={
            <>
              Jump to… <KbdChip label="Cmd + k" sx={{ ml: 1 }} />
            </>
          }
        >
          <LoopedSquareIcon />
        </IconButton>

        <IconButton
          onClick={handleLogout}
          sx={iconButtonSx}
          size="large"
          tooltipProps={{ theme: 'light' }}
          tooltip="Log out"
        >
          <LogOutIcon />
        </IconButton>
      </Box>
    </AppBar>
  );
}

function OrgInboxDrawer({
  open,
  onClose,
  org,
}: {
  open: boolean;
  onClose: () => void;
  org: GQOrgFieldsForAdminNavFragment;
}) {
  return (
    <SideDrawer open={open} onClose={onClose}>
      <Box component="header" display="flex" alignItems="center">
        <Typography variant="h1">{org.name}</Typography>
        <Box flexGrow={1} />
        <OrgQuickLinks org={org} flexWrap="wrap" alignItems="center" gap={0.25}>
          <LoginAsButtons targetOrgId={org.id} disabled={!org.hasUsers}>
            Login
          </LoginAsButtons>
          <IconButton onClick={onClose} style={{ padding: 4 }} size="large">
            <CloseIcon size={24} />
          </IconButton>
        </OrgQuickLinks>
      </Box>
      <Box
        component="section"
        position="relative"
        marginTop={2}
        paddingTop={2}
        borderTop={1}
        borderColor="divider"
      >
        <OrganizationInbox
          orgId={org.id}
          compact
          tableProps={{ marginX: -2, marginTop: 3 }}
        />
      </Box>
    </SideDrawer>
  );
}

function SideDrawer({
  open,
  onClose,
  children,
}: React.PropsWithChildren<{ open: boolean; onClose: () => void }>) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        root: {
          style: {
            zIndex: 1300,
          },
        },
        paper: {
          sx: {
            width: 'clamp(842px, 50vw, 1536px)',
            padding: 2,
          },
          onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
        },
      }}
    >
      {children}
    </Drawer>
  );
}
